export function formatTime(t: string | null | undefined): string {
  if (!t) return "—";
  const [hStr, mStr] = t.trim().split(":");
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr ?? "0", 10);
  if (isNaN(h)) return "—";
  const period = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${String(isNaN(m) ? 0 : m).padStart(2, "0")} ${period}`;
}

export function formatTimeRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  if (!start || !end) return "—";
  return `${formatTime(start)} – ${formatTime(end)}`;
}

/**
 * Compute minutes between two HH:MM or HH:MM:SS time strings.
 * Pure: null/invalid/non-positive → null. Never 0, never NaN.
 */
export function minutesBetween(
  start: string | null | undefined,
  end: string | null | undefined,
): number | null {
  if (!start || !end) return null;
  const parse = (t: string): number | null => {
    const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(t.trim());
    if (!m) return null;
    const h = Number(m[1]);
    const mm = Number(m[2]);
    if (h < 0 || h > 23 || mm < 0 || mm > 59) return null;
    return h * 60 + mm;
  };
  const s = parse(start);
  const e = parse(end);
  if (s == null || e == null) return null;
  const diff = e - s;
  return diff > 0 ? diff : null;
}

export function toMinutes(t: string): number {
  if (!t) return NaN;
  const clean = t.trim();
  const isPM = /pm/i.test(clean);
  const isAM = /am/i.test(clean);
  const [hStr, mStr] = clean.replace(/[apm]/gi, "").trim().split(":");
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr ?? "0", 10);
  if (isPM && h !== 12) h += 12;
  if (isAM && h === 12) h = 0;
  return h * 60 + (isNaN(m) ? 0 : m);
}

export interface OccupiedSlotLike {
  start_time: string;
  end_time: string;
  row_id?: string;
  source?: string;
}

/**
 * Returns true if [newStart, newEnd) overlaps any occupied slot on the same day.
 * Touching boundaries (e.g. 11:00 next to 11:00) do NOT overlap.
 * If excludeId is provided, slots with matching row_id are ignored (self-edit).
 */
export function hasTimeOverlap(
  newStart: string,
  newEnd: string,
  occupied: OccupiedSlotLike[],
  excludeId?: string,
): boolean {
  if (!newStart || !newEnd) return false;
  const ns = toMinutes(newStart);
  const ne = toMinutes(newEnd);
  if (isNaN(ns) || isNaN(ne)) return false;
  return (occupied ?? [])
    .filter((s) => !(excludeId && s.row_id === excludeId))
    .some((slot) => {
      const ss = toMinutes(slot.start_time);
      const se = toMinutes(slot.end_time);
      return ns < se && ne > ss;
    });
}
